import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/services/api';

interface Change {
  paragraph_index: number;
  blank_id: number;
  original: string;
  modified: string;
  question: string;
  confidence: number;
  source_doc_id?: number;
}

interface Phase2Annotation {
  topic: string;
  severity: 'CRITICAL' | 'MEDIUM' | 'LOW';
  buyer_text: string;
  seller_text: string;
  differences: string[];
  commercial_impact: string;
  recommendation: string;
  section_reference: string;
  buyer_paragraph_index?: number;
  buyer_paragraph_end?: number;
}

interface Annotation {
  type: 'phase1' | 'phase2';
  paragraphIndex: number;
  paragraphEnd?: number;
  severity?: 'CRITICAL' | 'MEDIUM' | 'LOW';
  data: Change | Phase2Annotation;
  originalText: string;
}

interface DORAAnnotatedEditorProps {
  projectId: number;
  documentId: number;
  changes: Change[];
  phase2Data?: {
    differences: Phase2Annotation[];
  };
  onBack?: () => void;
}

export default function DORAAnnotatedEditor({
  projectId,
  documentId,
  changes,
  phase2Data,
  onBack
}: DORAAnnotatedEditorProps) {
  const [content, setContent] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [currentAnnotationIndex, setCurrentAnnotationIndex] = useState(0);
  const [expandedAnnotations, setExpandedAnnotations] = useState<Set<number>>(new Set());
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [intelligentSummary, setIntelligentSummary] = useState<string | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [documentWidth, setDocumentWidth] = useState(() => {
    const saved = localStorage.getItem('dora-document-width');
    return saved ? parseFloat(saved) : 60;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Persist documentWidth to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('dora-document-width', documentWidth.toString());
  }, [documentWidth]);

  // Update editor HTML only when not actively editing
  useEffect(() => {
    if (editorRef.current && document.activeElement !== editorRef.current) {
      // Find last non-empty paragraph to avoid rendering trailing whitespace
      let lastContentIndex = content.length - 1;
      while (lastContentIndex >= 0 && !content[lastContentIndex]?.trim()) {
        lastContentIndex--;
      }
      
      console.log('🔍 Rendering document:', {
        totalParagraphs: content.length,
        lastContentIndex,
        trailingEmpty: content.length - lastContentIndex - 1
      });
      
      const html = content.slice(0, lastContentIndex + 1).map((paragraph, idx) => {
        const { annotation } = isHighlighted(idx);
        const isCurrentAnnotation = annotation && annotations[currentAnnotationIndex] === annotation;
        
        let style = 'padding: 8px; border-radius: 4px;';
        // Only add margin-bottom if not the last paragraph
        if (idx < lastContentIndex) {
          style += ' margin-bottom: 4px;';
        }
        
        if (annotation?.type === 'phase1') {
          // Phase I fills: Green
          style += ' background-color: #dcfce7; border-left: 4px solid #22c55e;';
        } else if (annotation?.type === 'phase2') {
          // Phase II differences: Red for CRITICAL, Yellow for MEDIUM/LOW
          if (annotation.severity === 'CRITICAL') {
            style += ' background-color: #fee2e2; border-left: 4px solid #ef4444;';
          } else {
            style += ' background-color: #fef3c7; border-left: 4px solid #eab308;';
          }
        }
        if (isCurrentAnnotation) {
          style += ' box-shadow: 0 0 0 2px #3b82f6;';
        }
        
        return `<div data-paragraph-index="${idx}" style="${style}">${paragraph || '&nbsp;'}</div>`;
      }).join('');
      
      editorRef.current.innerHTML = html;
      
      // Add click handlers to highlighted paragraphs
      const highlightedParagraphs = editorRef.current.querySelectorAll('[data-paragraph-index]');
      highlightedParagraphs.forEach(paragraph => {
        const paragraphIndex = parseInt(paragraph.getAttribute('data-paragraph-index') || '-1');
        const { annotation } = isHighlighted(paragraphIndex);
        
        if (annotation) {
          // Make it clickable
          (paragraph as HTMLElement).style.cursor = 'pointer';
          
          // Add click handler
          paragraph.addEventListener('click', () => {
            const annotationIndex = annotations.findIndex(a => a === annotation);
            if (annotationIndex !== -1) {
              console.log(`🖱️ Clicked paragraph ${paragraphIndex}, jumping to annotation ${annotationIndex}`);
              setCurrentAnnotationIndex(annotationIndex);
            }
          });
        }
      });
    }
  }, [content, annotations, currentAnnotationIndex]);

  // Auto-update side panel when scrolling to Phase II paragraphs
  useEffect(() => {
    if (!contentRef.current || !editorRef.current || annotations.length === 0) return;
  }, [annotations, content]);

  // Build annotations list from Phase I changes and Phase II differences
  useEffect(() => {
    // Only create annotations when we have content loaded
    if (content.length === 0) return;

    const allAnnotations: Annotation[] = [];

    // Phase I: Green fills
    changes.forEach(change => {
      allAnnotations.push({
        type: 'phase1',
        paragraphIndex: change.paragraph_index,
        data: change,
        originalText: content[change.paragraph_index] || ''
      });
    });

    // Phase II: Red highlights for differences
    if (phase2Data?.differences) {
      phase2Data.differences.forEach(diff => {
        if (diff.buyer_paragraph_index !== undefined) {
          allAnnotations.push({
            type: 'phase2',
            paragraphIndex: diff.buyer_paragraph_index,
            paragraphEnd: diff.buyer_paragraph_end,
            severity: diff.severity,
            data: diff,
            originalText: content[diff.buyer_paragraph_index] || ''
          });
        }
      });
    }

    // Sort by paragraph index, then by type (phase1 first, then phase2)
    allAnnotations.sort((a, b) => {
      if (a.paragraphIndex !== b.paragraphIndex) {
        return a.paragraphIndex - b.paragraphIndex;
      }
      // If same paragraph, phase1 comes before phase2
      return a.type === 'phase1' ? -1 : 1;
    });
    
    // Count unique paragraphs for navigation
    const uniqueParagraphs = new Set(allAnnotations.map(a => a.paragraphIndex)).size;
    
    console.log(`📊 Annotations rebuilt:`);
    console.log(`   - Phase I changes: ${changes.length}`);
    console.log(`   - Phase II differences: ${phase2Data?.differences?.length || 0}`);
    console.log(`   - Total items: ${allAnnotations.length}`);
    console.log(`   - Unique paragraphs: ${uniqueParagraphs}`);
    console.log(`   - Items per paragraph: ${(allAnnotations.length / uniqueParagraphs).toFixed(1)} avg`);
    
    setAnnotations(allAnnotations);
  }, [changes, phase2Data, content]);

  // Fetch document content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/documents/${documentId}/content`);
        
        // Backend returns object with paragraphs array
        if (response.data.paragraphs && Array.isArray(response.data.paragraphs)) {
          setContent(response.data.paragraphs);
        } else if (Array.isArray(response.data)) {
          setContent(response.data);
        } else if (typeof response.data === 'string') {
          setContent(response.data.split('\n'));
        } else if (response.data.content && typeof response.data.content === 'string') {
          setContent(response.data.content.split('\n'));
        } else {
          console.error('Unexpected response format:', response.data);
          setContent(['Error loading document']);
        }
      } catch (error) {
        console.error('Failed to fetch document:', error);
        setContent(['Failed to load document. Please try again.']);
      } finally {
        setLoading(false);
      }
    };

    if (documentId) {
      fetchContent();
    }
  }, [documentId]);

  // Scroll to current annotation when index changes (but not during editing)
  useEffect(() => {
    // Only scroll if user is not actively editing
    if (editorRef.current && document.activeElement === editorRef.current) {
      // User is typing - don't interrupt with auto-scroll
      return;
    }
    
    if (annotations.length > 0 && currentAnnotationIndex < annotations.length) {
      const annotation = annotations[currentAnnotationIndex];
      if (annotation && contentRef.current) {
        // Find the paragraph div by data-paragraph-index attribute within the scroll area
        const paragraphDiv = contentRef.current.querySelector(`[data-paragraph-index="${annotation.paragraphIndex}"]`);
        
        console.log(`📍 Scrolling to annotation ${currentAnnotationIndex}, paragraph ${annotation.paragraphIndex}`, paragraphDiv);
        
        if (paragraphDiv) {
          paragraphDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          console.warn(`⚠️ Could not find paragraph element for index ${annotation.paragraphIndex}`);
        }
      }
    }
  }, [currentAnnotationIndex]);

  // Fetch pre-generated intelligent summary when Analysis Summary is opened
  useEffect(() => {
    if (showSummary && !intelligentSummary && !loadingSummary && projectId) {
      setLoadingSummary(true);
      const fetchSummary = async () => {
        try {
          const response = await api.get(`/dora/projects/${projectId}/analysis-summary`);
          setIntelligentSummary(response.data.summary);
        } catch (error) {
          console.error('Failed to fetch analysis summary:', error);
        } finally {
          setLoadingSummary(false);
        }
      };
      fetchSummary();
    }
  }, [showSummary, intelligentSummary, loadingSummary, projectId]);

  // Handle resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      
      // Constrain between 30% and 80%
      if (newWidth >= 30 && newWidth <= 80) {
        setDocumentWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleNext = () => {
    console.log(`🔍 NEXT clicked - Current index: ${currentAnnotationIndex}, Total: ${annotations.length}`);
    if (currentAnnotationIndex < annotations.length - 1) {
      const currentParagraph = annotations[currentAnnotationIndex].paragraphIndex;
      
      // Find the next annotation on a DIFFERENT paragraph
      let newIndex = currentAnnotationIndex + 1;
      while (newIndex < annotations.length && annotations[newIndex].paragraphIndex === currentParagraph) {
        newIndex++;
      }
      
      // If we found a different paragraph, navigate to it
      if (newIndex < annotations.length) {
        console.log(`   ➡️  Moving to index ${newIndex} (paragraph ${annotations[newIndex].paragraphIndex})`);
        setCurrentAnnotationIndex(newIndex);
      } else {
        console.log(`   ⚠️  Already at last paragraph`);
      }
    } else {
      console.log(`   ⚠️  Already at last annotation`);
    }
  };

  const handlePrevious = () => {
    console.log(`🔍 PREVIOUS clicked - Current index: ${currentAnnotationIndex}, Total: ${annotations.length}`);
    if (currentAnnotationIndex > 0) {
      const currentParagraph = annotations[currentAnnotationIndex].paragraphIndex;
      
      // Find the previous annotation on a DIFFERENT paragraph
      let newIndex = currentAnnotationIndex - 1;
      while (newIndex >= 0 && annotations[newIndex].paragraphIndex === currentParagraph) {
        newIndex--;
      }
      
      // If we found a different paragraph, navigate to it
      if (newIndex >= 0) {
        console.log(`   ⬅️  Moving to index ${newIndex} (paragraph ${annotations[newIndex].paragraphIndex})`);
        setCurrentAnnotationIndex(newIndex);
      } else {
        console.log(`   ⚠️  Already at first paragraph`);
      }
    } else {
      console.log(`   ⚠️  Already at first annotation`);
    }
  };


  const copyRecommendation = async (recommendation: string, index: number) => {
    try {
      await navigator.clipboard.writeText(recommendation);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const isHighlighted = (paragraphIndex: number): { type: 'green' | 'red' | null; annotation: Annotation | null } => {
    // Check Phase I (green)
    const phase1Match = annotations.find(
      a => a.type === 'phase1' && a.paragraphIndex === paragraphIndex
    );
    if (phase1Match) {
      return { type: 'green', annotation: phase1Match };
    }

    // Check Phase II (red) - including range
    const phase2Match = annotations.find(
      a => a.type === 'phase2' && 
      paragraphIndex >= a.paragraphIndex && 
      paragraphIndex <= (a.paragraphEnd || a.paragraphIndex)
    );
    if (phase2Match) {
      return { type: 'red', annotation: phase2Match };
    }

    return { type: null, annotation: null };
  };


  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading document...</div>
        </CardContent>
      </Card>
    );
  }

  const currentAnnotation = annotations[currentAnnotationIndex];

  return (
    <div className="space-y-4">
      {/* Navigation Header - Compact Single Line */}
      <Card className="sticky top-0 z-10 bg-background shadow-md">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Back Button */}
            {onBack && (
              <Button
                onClick={onBack}
                variant="outline"
                size="sm"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            
            {/* Title with save indicator */}
            <div className="flex items-center gap-2">
              <CardTitle className="text-base whitespace-nowrap">
                DORA Document Editor
              </CardTitle>
              {isSaving && (
                <span className="text-xs text-muted-foreground">Saving...</span>
              )}
              {!isSaving && lastSaved && (
                <span className="text-xs text-muted-foreground">
                  Saved {new Date().getTime() - lastSaved.getTime() < 60000 
                    ? 'just now' 
                    : `${Math.floor((new Date().getTime() - lastSaved.getTime()) / 60000)}m ago`}
                </span>
              )}
            </div>
            
            {/* Previous Button */}
            <Button
              onClick={handlePrevious}
              disabled={currentAnnotationIndex === 0}
              size="sm"
              variant="outline"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            
            {/* Counter & Badge */}
            <div className="text-sm text-muted-foreground whitespace-nowrap">
              {annotations.length > 0 ? (
                <>
                  {(() => {
                    // Calculate current paragraph position and total unique paragraphs
                    const currentParagraph = annotations[currentAnnotationIndex]?.paragraphIndex ?? 0;
                    const uniqueParagraphs = [...new Set(annotations.map(a => a.paragraphIndex))];
                    const currentParagraphPosition = uniqueParagraphs.indexOf(currentParagraph) + 1;
                    return `${currentParagraphPosition} of ${uniqueParagraphs.length}`;
                  })()}
                  {currentAnnotation && (
                    <Badge 
                      className="ml-2" 
                      variant={
                        currentAnnotation.type === 'phase1' ? 'default' : 
                        currentAnnotation.severity === 'CRITICAL' ? 'destructive' : 'default'
                      }
                      style={
                        currentAnnotation.type === 'phase2' && currentAnnotation.severity !== 'CRITICAL' 
                          ? { backgroundColor: '#fef3c7', color: '#854d0e', borderColor: '#eab308' }
                          : undefined
                      }
                    >
                      {currentAnnotation.type === 'phase1' ? 'Fill' : currentAnnotation.severity}
                    </Badge>
                  )}
                </>
              ) : (
                'No annotations'
              )}
            </div>
            
            {/* Next Button */}
            <Button
              onClick={handleNext}
              disabled={currentAnnotationIndex >= annotations.length - 1}
              size="sm"
              variant="outline"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            
            {/* Summary Toggle */}
            <Button
              onClick={() => setShowSummary(!showSummary)}
              variant="outline"
              size="sm"
            >
              {showSummary ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
              Summary
            </Button>
          </div>
        </CardHeader>
        {showSummary && phase2Data && (
          <CardContent className="pt-0 border-t">
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {phase2Data.differences?.length || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Total Issues</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {phase2Data.differences?.filter(d => d.severity === 'CRITICAL').length || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Critical</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {annotations.filter(a => a.type === 'phase1').length}
                  </div>
                  <div className="text-xs text-muted-foreground">Fills</div>
                </div>
              </div>
              
              {/* LLM-Generated Summary */}
              {loadingSummary ? (
                <div className="bg-gray-50 border border-gray-200 rounded p-4 text-center">
                  <div className="text-sm text-gray-500">Loading analysis summary...</div>
                </div>
              ) : intelligentSummary && (
                <div className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden max-h-[600px] overflow-y-auto">
                  <style dangerouslySetInnerHTML={{ __html: `
                    .summary-content {
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                      line-height: 1.7;
                    }
                    .summary-content h3,
                    .summary-content h4,
                    .summary-content h5 {
                      font-weight: 700;
                      margin-top: 1.5rem;
                      margin-bottom: 0.75rem;
                    }
                    .summary-content h3 {
                      font-size: 1.25rem;
                      color: #1e40af;
                      border-bottom: 3px solid #3b82f6;
                      padding-bottom: 0.5rem;
                    }
                    .summary-content h4 {
                      font-size: 1.1rem;
                      color: #1f2937;
                      border-bottom: 2px solid #e5e7eb;
                      padding-bottom: 0.4rem;
                    }
                    .summary-content h5 {
                      font-size: 1rem;
                      color: #374151;
                    }
                    .summary-content p {
                      margin: 0.75rem 0;
                      color: #374151;
                    }
                    .summary-content ul,
                    .summary-content ol {
                      margin: 0.75rem 0;
                      padding-left: 1.75rem;
                    }
                    .summary-content li {
                      margin: 0.5rem 0;
                      color: #374151;
                    }
                    .summary-content strong {
                      font-weight: 600;
                      color: #1f2937;
                    }
                    .summary-content .summary-header {
                      background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
                      color: white;
                      padding: 1.5rem;
                      margin: 0 0 1.5rem 0;
                    }
                    .summary-content .summary-header h3 {
                      color: white;
                      margin: 0;
                      border: none;
                    }
                    .summary-content .summary-header .meta {
                      font-size: 0.875rem;
                      opacity: 0.9;
                      margin-top: 0.5rem;
                    }
                    .summary-content .overview-section,
                    .summary-content .priorities-section,
                    .summary-content .strategic-section,
                    .summary-content .deliverables-section,
                    .summary-content .actions-section,
                    .summary-content .recommendation-section {
                      padding: 1.25rem;
                      margin: 1rem 0;
                      border-radius: 0.5rem;
                    }
                    .summary-content .overview-section {
                      background: #f0f9ff;
                      border-left: 4px solid #3b82f6;
                    }
                    .summary-content .priorities-section {
                      background: #fef2f2;
                      border-left: 4px solid #ef4444;
                    }
                    .summary-content .priority-item {
                      background: white;
                      border: 1px solid #fecaca;
                      border-radius: 0.5rem;
                      padding: 1rem;
                      margin: 0.75rem 0;
                    }
                    .summary-content .strategic-section {
                      background: #fefce8;
                      border-left: 4px solid #eab308;
                    }
                    .summary-content .deliverables-section {
                      background: #f0fdf4;
                      border-left: 4px solid #22c55e;
                    }
                    .summary-content .actions-section {
                      background: #faf5ff;
                      border-left: 4px solid #a855f7;
                    }
                    .summary-content .recommendation-section {
                      background: #eff6ff;
                      border-left: 4px solid #3b82f6;
                    }
                  ` }} />
                  <div 
                    className="summary-content p-6 text-sm"
                    dangerouslySetInnerHTML={{ 
                      __html: intelligentSummary
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n\n/g, '</p><p>')
                        .replace(/^(.+)$/gm, (match) => {
                          if (match.startsWith('**')) return match;
                          return `<p>${match}</p>`;
                        })
                    }}
                  />
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Document Content and Recommendation Panel Layout */}
      <div className="flex gap-0" ref={containerRef}>
        {/* Main Document Editor - Resizable with consistent width */}
        <Card 
          className="flex-shrink-0"
          style={{ width: `${documentWidth}%` }}
        >
          <CardContent className="p-6">
            <ScrollArea className="h-[600px]" ref={contentRef}>
              <div
                ref={editorRef}
                contentEditable={true}
                suppressContentEditableWarning
                onInput={(e) => {
                  // Auto-save on input with debounce
                  const text = e.currentTarget.innerText;
                  const lines = text.split('\n');
                  setContent(lines);
                  
                  // Remap annotations immediately to keep highlights in sync
                  const remappedAnnotations = annotations.map(annotation => {
                    // Try exact match first
                    let newIndex = lines.findIndex(line => 
                      line.trim() === annotation.originalText.trim()
                    );
                    
                    // If exact match fails, try fuzzy match (contains original text)
                    if (newIndex === -1) {
                      newIndex = lines.findIndex(line => 
                        line.includes(annotation.originalText.trim()) || 
                        annotation.originalText.trim().includes(line.trim())
                      );
                    }
                    
                    // If still not found, try to find by proximity to original index
                    if (newIndex === -1) {
                      // Check if paragraph at original index still exists
                      if (annotation.paragraphIndex < lines.length) {
                        newIndex = annotation.paragraphIndex;
                      }
                    }
                    
                    if (newIndex !== -1) {
                      return {
                        ...annotation,
                        paragraphIndex: newIndex
                      };
                    }
                    
                    // Keep original if we can't find it
                    return annotation;
                  });
                  
                  setAnnotations(remappedAnnotations);
                  
                  // Clear existing timeout
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                  }
                  
                  // Set new timeout to save after 2 seconds of no typing
                  saveTimeoutRef.current = setTimeout(async () => {
                    try {
                      setIsSaving(true);
                      await api.put(`/dora/documents/${documentId}/save-edits`, {
                        paragraphs: lines
                      });
                      setLastSaved(new Date());
                      console.log('✅ Document auto-saved');
                    } catch (error) {
                      console.error('Failed to auto-save document:', error);
                    } finally {
                      setIsSaving(false);
                    }
                  }, 2000);
                }}
                onBlur={(e) => {
                  const text = e.currentTarget.innerText;
                  const lines = text.split('\n');
                  setContent(lines);
                  
                  // Remap annotation indices to match new content positions
                  const remappedAnnotations = annotations.map(annotation => {
                    // Find where the original text now appears in the new content
                    const newIndex = lines.findIndex(line => 
                      line.trim() === annotation.originalText.trim()
                    );
                    
                    if (newIndex !== -1) {
                      return {
                        ...annotation,
                        paragraphIndex: newIndex
                      };
                    }
                    // If exact match not found, keep original index
                    return annotation;
                  });
                  
                  setAnnotations(remappedAnnotations);
                  
                  // Force save on blur
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                  }
                  api.put(`/dora/documents/${documentId}/save-edits`, {
                    paragraphs: lines
                  }).then(() => {
                    setLastSaved(new Date());
                    console.log('✅ Document saved on blur');
                  }).catch(error => {
                    console.error('Failed to save document:', error);
                  });
                }}
                className="font-mono text-sm outline-none whitespace-pre-wrap break-words overflow-wrap-anywhere"
                style={{ display: 'block', lineHeight: '1.5', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              />
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Resizable Divider */}
        {annotations[currentAnnotationIndex]?.type === 'phase2' && (
          <div
            className="w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize flex-shrink-0 transition-colors"
            onMouseDown={() => setIsResizing(true)}
            style={{ cursor: 'col-resize' }}
          />
        )}

        {/* Phase II Recommendation Side Panel */}
        {annotations[currentAnnotationIndex]?.type === 'phase2' && (() => {
          const isCritical = annotations[currentAnnotationIndex].severity === 'CRITICAL';
          const borderColor = isCritical ? 'border-red-500' : 'border-yellow-500';
          const bgColor = isCritical ? 'bg-red-50' : 'bg-yellow-50';
          const textColor = isCritical ? 'text-red-900' : 'text-yellow-900';
          const badgeBorderColor = isCritical ? 'border-red-200' : 'border-yellow-200';
          
          return (
            <Card 
              className="flex-shrink-0"
              style={{ width: `${100 - documentWidth}%` }}
            >
              <CardContent className="p-4">
                <div className={`border-l-4 ${borderColor} ${bgColor} p-3 rounded`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className={`font-semibold ${textColor} mb-1`}>
                        {(annotations[currentAnnotationIndex].data as Phase2Annotation).topic.replace(/_/g, ' ').toUpperCase()}
                      </div>
                    </div>
                    <Badge 
                      variant={isCritical ? 'destructive' : 'default'}
                      style={
                        !isCritical 
                          ? { backgroundColor: '#fef3c7', color: '#854d0e', borderColor: '#eab308' }
                          : undefined
                      }
                    >
                      {annotations[currentAnnotationIndex].severity}
                    </Badge>
                  </div>

                  <div className="space-y-3">
                    {/* Buyer's Requirement */}
                    <div className={`${bgColor} p-3 rounded border ${badgeBorderColor}`}>
                      <div className={`text-xs font-semibold ${textColor} mb-1`}>BUYER'S REQUIREMENT:</div>
                      <div className="text-sm text-gray-700">
                        {(annotations[currentAnnotationIndex].data as Phase2Annotation).buyer_text || 'N/A'}
                      </div>
                    </div>
                  
                  {/* Seller's Standard Position */}
                  <div className="bg-blue-50 p-3 rounded border border-blue-200">
                    <div className="text-xs font-semibold text-blue-900 mb-1">SELLER'S STANDARD POSITION:</div>
                    <div className="text-sm text-gray-700">
                      {(annotations[currentAnnotationIndex].data as Phase2Annotation).seller_text || 'N/A'}
                    </div>
                  </div>
                  
                  {/* Recommended Counter-Proposal */}
                  <div className="bg-green-50 p-3 rounded border border-green-200">
                    <div className="text-xs font-semibold text-green-900 mb-1">RECOMMENDED COUNTER-PROPOSAL:</div>
                    <div className="text-sm whitespace-pre-wrap text-gray-700">
                      {(annotations[currentAnnotationIndex].data as Phase2Annotation).recommendation}
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => {
                      const annotation = annotations[currentAnnotationIndex];
                      if (annotation?.data) {
                        copyRecommendation(
                          (annotation.data as Phase2Annotation).recommendation,
                          currentAnnotationIndex
                        );
                      }
                    }}
                    size="sm"
                    variant="outline"
                    className="w-full"
                  >
                    {copiedIndex === currentAnnotationIndex ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Counter-Proposal
                      </>
                    )}
                  </Button>
                  
                  <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                    <strong>Commercial Impact:</strong> {(annotations[currentAnnotationIndex].data as Phase2Annotation).commercial_impact}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })()}
      </div>
    </div>
  );
}
